import {
  CalendarDateTime,
  fromDate,
  getLocalTimeZone,
  toCalendarDateTime,
} from "@internationalized/date";
import { formatTime } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { z } from "zod";

const openEndedVisibilitySchemaResponse = z.object({
  type: z.literal("OPEN_ENDED"),
  fromTime: z.coerce
    .date()
    .transform((time) =>
      toCalendarDateTime(fromDate(time, getLocalTimeZone())),
    ),
});
const timedVisibilitySchemaResponse = z.object({
  type: z.literal("TIMED"),
  fromTime: z.coerce
    .date()
    .transform((time) =>
      toCalendarDateTime(fromDate(time, getLocalTimeZone())),
    ),
  toTime: z.coerce
    .date()
    .transform((time) =>
      toCalendarDateTime(fromDate(time, getLocalTimeZone())),
    ),
});
const constantVisibilitySchemaResponse = z.object({
  type: z.literal("CONSTANT"),
  visible: z.boolean(),
});

export const visibilitySchemaResponse = z.discriminatedUnion("type", [
  openEndedVisibilitySchemaResponse,
  timedVisibilitySchemaResponse,
  constantVisibilitySchemaResponse,
]);

export type VisibilityResponse = z.infer<typeof visibilitySchemaResponse>;

export const openEndedVisibilityFormSchema = z.object({
  type: z.literal("OPEN_ENDED"),
  fromTime: z.custom<CalendarDateTime>(),
});
const timedVisibilityFormSchema = z.object({
  type: z.literal("TIMED"),
  fromTime: z.custom<CalendarDateTime>(),
  toTime: z.custom<CalendarDateTime>(),
});

export type TimedVisibilityFormSchema = z.infer<
  typeof timedVisibilityFormSchema
>;
const constantVisibilityFormSchema = z.object({
  type: z.literal("CONSTANT"),
  visible: z.boolean(),
});

export const visibilityFormSchema = z
  .discriminatedUnion("type", [
    openEndedVisibilityFormSchema,
    timedVisibilityFormSchema,
    constantVisibilityFormSchema,
  ])
  .refine(
    (data) => {
      if (data.type === "TIMED") {
        return data.fromTime <= data.toTime;
      }
      return true;
    },
    {
      message: "Synlighet må settes fram i tid",
    },
  );

export type VisibilityForm = z.infer<typeof visibilityFormSchema>;
const constantVisibilityRequest = z.object({
  type: z.literal("CONSTANT"),
  visible: z.boolean(),
});
const openEndedVisibilityRequest = z.object({
  type: z.literal("OPEN_ENDED"),
  fromTime: z.string(),
});
const timedVisibilityRequest = z.object({
  type: z.literal("TIMED"),
  fromTime: z.string(),
  toTime: z.string(),
});

export const visibilityRequest = z.discriminatedUnion("type", [
  constantVisibilityRequest,
  openEndedVisibilityRequest,
  timedVisibilityRequest,
]);

export type VisibilityRequest = z.infer<typeof visibilityRequest>;

export const visibilityFormToRequest = (
  visibility: VisibilityForm,
): VisibilityRequest => {
  switch (visibility.type) {
    case "CONSTANT":
      return {
        type: "CONSTANT",
        visible: true,
      };
    case "TIMED":
      return {
        type: "TIMED",
        fromTime: formatTime(visibility.fromTime),
        toTime: formatTime(visibility.toTime),
      };
    case "OPEN_ENDED":
      return {
        type: "OPEN_ENDED",
        fromTime: formatTime(visibility.fromTime),
      };
  }
};
